
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output, ViewChild, inject } from '@angular/core';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { IonicImports, MaterialImportsModule } from 'src/app/material-imports.module';
import { IAnnotationAttachment } from 'src/app/model/thingie/annotation';
import { ThingieService } from 'src/app/services/thingie/thingie-service.service';
import { areSameOrigin } from 'src/app/utility/are-same-origin';
import { DefaultConfig } from 'src/assets/default.config';
import { ThumbnailComponent } from '../../images/thumbnail/thumbnail.component';
import { IDownloadableAttachment } from '../model/downloadable-attachment';
import { isTemporaryAttachment } from '../model/temporary-attachment';

/**
 * Attachment, which will have it's file extensions displayed
 * instead of a preview, since it's media type is not supported
 * for previewing.
 */
interface IExtensionPreview extends IDownloadableAttachment {
  previewType: 'file-extension';

  /**
   * The file extension to show in place of the thumbnail
   */
  fileExtension: string | null;
}

/**
 * Attachment, which will have it's file preview displayed
 */
interface IMediaPreview extends IDownloadableAttachment {
  previewType: 'media';

  /**
   * URI fed to the thumbnail component
   */
  previewUri: string;
}

/**
 * Type used for feeding data to the table of attachments
 *
 * @see {@link AnnotationAttachmentListComponent}
 */
type TableDataType = IExtensionPreview | IMediaPreview;


/**
 * List of annotations' attachments
 *
 * Displays a list of attachments with either
 * the preview of the attachment content or the
 * file extension as text.
 *
 * The files can be downloaded directly from the list,
 * by pressing on the file names/links.
 *
 * If `actionsEnabled` is `true` an actions-column is shown
 * which currently only supports deletion of individual attachments.
 *
 * Note: input `attachments` is required
 */
@Component({
  selector: 'app-annotation-attachment-list[attachments]',
  templateUrl: './annotation-attachment-list.component.html',
  styleUrls: ['./annotation-attachment-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IonicImports,
    MaterialImportsModule,
    ThumbnailComponent,
  ]
})
export class AnnotationAttachmentListComponent {

  /**
   * @private
   */
  @ViewChild(MatTable, { static: true })
  private table!: MatTable<TableDataType>;

  /**
   * List of downloadable attachments to display
   */
  @Input({ required: true })
  public set attachments(value: IDownloadableAttachment[]) {
    this.updateTableData(value);
  }

  /**
   * Whether to show the actions column
   */
  @Input()
  public set actionsEnabled(value: boolean) {
    if (value) {
      this.displayedColumns.push('actions');
    } else {
      const idx = this.displayedColumns.findIndex(it => it === 'actions');
      if (idx !== -1) {
        this.displayedColumns.splice(idx, 1);
      }
    }
  }

  /**
   * Emits when an attachment or temporary attachment (File)
   * is marked for removal
   */
  @Output()
  public removeAttachment = new EventEmitter<IAnnotationAttachment>();

  /**
   * Columns to display in the table
   *
   * @private
   */
  protected displayedColumns = ['file', 'preview'];

  /**
   * Data source for the table
   *
   * @private
   */
  protected dataSource = new MatTableDataSource<TableDataType>([]);

  private thingieService = inject(ThingieService);

  /**
   * Takes downloadable attachments and converts them to table data
   * which is then added to the table's data source
   *
   * @param value
   */
  private updateTableData(value: IDownloadableAttachment[]): void {
    const tableData = value
      .filter(it => !it.deleted)
      .map(it => this.convertAttachmentToTableEntry(it));

    this.dataSource.data = tableData;
  }

  /**
   * Convert attachment to table entry data-type object
   *
   * Preserve the original attachment object
   *
   * @param attachment
   * @returns Object of type {@link TableDataType}
   */
  private convertAttachmentToTableEntry(attachment: IDownloadableAttachment): TableDataType {
    const [mimetypeMainType] = attachment.mimetype.split('/');
    switch (mimetypeMainType) {
      // case 'video':
      case 'image': {
        let previewUri: string;
        if (isTemporaryAttachment(attachment)) {
          previewUri = attachment.downloadUri;
        } else {
          previewUri = DefaultConfig.uris.thingieAttachmentPreview
            .replace('{hash}', attachment.sha256Hash);
        }

        const result: IMediaPreview = {
          ...attachment,
          previewType: 'media',
          previewUri
        };

        return result;
      }
      default: {
        const parts = attachment.originalFileName.split('.');
        const result: IExtensionPreview = {
          ...attachment,
          previewType: 'file-extension',
          fileExtension: parts.length > 1 ? parts.pop()! : null,

        };

        return result;
      }
    }
  }

  /**
   * Remove attachment
   *
   * @private
   * @param attachment The attachment to remove
   */
  protected _removeAttachment(attachment: TableDataType): void {
    this.removeAttachment.emit(attachment);
  }

  /**
   * Starts the download of the attachment if the browser
   * can not handle it natively.
   *
   * @private
   * @param $event
   * @param attachment
   */
  protected _downloadAttachment($event: MouseEvent, attachment: TableDataType): void {
    // Let the browser handle same-origin downloads
    if (areSameOrigin(window.location.origin, attachment.downloadUri)) {
      return;
    }

    $event.preventDefault();
    void this.thingieService.downloadAnnotationAttachment(attachment);
  }

  /**
   * TrackBy-Function to hopefully improve performance of the table
   *
   * @private
   * @param _ ignored - the index of the item
   * @param item The attachment to identify
   * @returns SHA256 hash of the attachment
   */
  protected _trackBy(_: number, item: TableDataType) {
    return item.sha256Hash;
  }
}

